import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

// 着色器模拟粒子
const ShaderParticle: React.FC<{
  x: number;
  y: number;
  size: number;
  color: string;
  delay: number;
  type: 'circle' | 'square' | 'triangle';
}> = ({ x, y, size, color, delay, type }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const progress = spring({
    frame: frame - delay,
    fps,
    config: {
      damping: 12,
      stiffness: 100,
    },
  });

  const scale = interpolate(progress, [0, 1], [0, 1]);
  const rotate = interpolate(progress, [0, 1], [0, 360]);
  const opacity = interpolate(frame - delay, [0, 10], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const renderShape = () => {
    switch (type) {
      case 'circle':
        return (
          <div
            style={{
              width: size,
              height: size,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${color} 0%, ${color}88 50%, transparent 100%)`,
              boxShadow: `0 0 ${size / 2}px ${color}`,
            }}
          />
        );
      case 'square':
        return (
          <div
            style={{
              width: size,
              height: size,
              background: `linear-gradient(135deg, ${color} 0%, ${color}88 100%)`,
              boxShadow: `0 0 ${size / 2}px ${color}`,
            }}
          />
        );
      case 'triangle':
        return (
          <div
            style={{
              width: 0,
              height: 0,
              borderLeft: `${size / 2}px solid transparent`,
              borderRight: `${size / 2}px solid transparent`,
              borderBottom: `${size}px solid ${color}`,
              filter: `drop-shadow(0 0 ${size / 4}px ${color})`,
            }}
          />
        );
    }
  };

  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        top: y,
        transform: `translate(-50%, -50%) scale(${scale}) rotate(${rotate}deg)`,
        opacity,
      }}
    >
      {renderShape()}
    </div>
  );
};

// 着色器层（模拟 GLSL 片段着色器效果）
const ShaderLayer: React.FC<{
  blendMode: string;
  color: string;
}> = ({ blendMode, color }) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();

  const wave = Math.sin(frame * 0.05) * 20;
  const pulse = interpolate(Math.sin(frame * 0.1), [-1, 1], [0.3, 0.6]);

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: `radial-gradient(circle at ${width / 2 + wave}px ${height / 2}px, ${color}00 0%, ${color}${Math.floor(pulse * 255).toString(16).padStart(2, '0')} 50%, transparent 100%)`,
        mixBlendMode: blendMode as any,
        pointerEvents: 'none',
      }}
    />
  );
};

// 发光文字
const GlowingText: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const scale = spring({
    frame,
    fps,
    config: {
      damping: 15,
      stiffness: 200,
    },
  });

  const glowIntensity = interpolate(
    Math.sin(frame * 0.2),
    [-1, 1],
    [0.8, 1.2]
  );

  return (
    <div
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: `translate(-50%, -50%) scale(${scale})`,
        textAlign: 'center',
        zIndex: 10,
      }}
    >
      <div
        style={{
          fontSize: 100,
          fontWeight: 'bold',
          background: 'linear-gradient(135deg, #00d4ff 0%, #7c3aed 50%, #f472b6 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          color: 'transparent',
          textShadow: `
            0 0 20px rgba(0, 212, 255, ${glowIntensity}),
            0 0 40px rgba(124, 58, 237, ${glowIntensity}),
            0 0 60px rgba(244, 114, 182, ${glowIntensity})
          `,
          letterSpacing: '0.1em',
        }}
      >
        SHADER
      </div>
      <div
        style={{
          fontSize: 28,
          color: '#94a3b8',
          marginTop: 15,
          fontWeight: '500',
        }}
      >
        WebGL GLSL Simulation
      </div>
    </div>
  );
};

// 扫描线效果
const ScanLines: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();

  const yOffset = (frame * 2) % height;

  return (
    <svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        opacity: 0.1,
        pointerEvents: 'none',
      }}
    >
      {Array.from({ length: 20 }).map((_, i) => (
        <line
          key={i}
          x1={0}
          y1={(yOffset + i * 50) % height}
          x2={width}
          y2={(yOffset + i * 50) % height}
          stroke="rgba(0, 212, 255, 0.5)"
          strokeWidth="1"
        />
      ))}
    </svg>
  );
};

export const WebGLShaderEffect: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();

  // 粒子配置
  const particleCount = 100;
  const colors = [
    '#00d4ff',
    '#7c3aed',
    '#f472b6',
    '#34d399',
    '#fbbf24',
  ];

  const shapes: Array<'circle' | 'square' | 'triangle'> = ['circle', 'square', 'triangle'];

  // 生成粒子
  const particles: Array<{
    x: number;
    y: number;
    size: number;
    color: string;
    delay: number;
    type: 'circle' | 'square' | 'triangle';
  }> = [];

  // 螺旋分布粒子
  for (let i = 0; i < particleCount; i++) {
    const angle = (i / particleCount) * Math.PI * 6; // 3 圈螺旋
    const radius = 100 + (i / particleCount) * 300;

    particles.push({
      x: width / 2 + Math.cos(angle) * radius,
      y: height / 2 + Math.sin(angle) * radius,
      size: 10 + Math.random() * 20,
      color: colors[i % colors.length],
      delay: i * 0.5,
      type: shapes[i % 3],
    });
  }

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #0a0a1a 0%, #1a0a2e 50%, #0a1a2e 100%)',
      }}
    >
      {/* 背景着色器层 */}
      <ShaderLayer blendMode="screen" color="#00d4ff" />
      <ShaderLayer blendMode="screen" color="#7c3aed" />
      <ShaderLayer blendMode="screen" color="#f472b6" />

      {/* 扫描线 */}
      <ScanLines />

      {/* 背景网格 */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            radial-gradient(circle at 2px 2px, rgba(0, 212, 255, 0.15) 1px, transparent 0)
          `,
          backgroundSize: '40px 40px',
        }}
      />

      {/* 着色器粒子 */}
      {particles.map((particle, index) => (
        <ShaderParticle key={index} {...particle} />
      ))}

      {/* 中心文字 */}
      <GlowingText />

      {/* 角落装饰 */}
      <div
        style={{
          position: 'absolute',
          top: 30,
          left: 30,
          fontSize: 12,
          color: '#00d4ff',
          fontFamily: 'monospace',
          opacity: 0.7,
        }}
      >
        {'>'} GLSL_SHADER.SIMULATION
      </div>

      <div
        style={{
          position: 'absolute',
          bottom: 30,
          right: 30,
          fontSize: 12,
          color: '#f472b6',
          fontFamily: 'monospace',
          opacity: 0.7,
        }}
      >
        PARTICLES: {particleCount} | FPS: 30
      </div>

      {/* GPU 加速指示器 */}
      <div
        style={{
          position: 'absolute',
          top: 30,
          right: 30,
          padding: '8px 16px',
          background: 'rgba(0, 212, 255, 0.1)',
          border: '1px solid rgba(0, 212, 255, 0.3)',
          borderRadius: 4,
          fontSize: 11,
          color: '#00d4ff',
          fontFamily: 'monospace',
        }}
      >
        GPU: ENABLED
      </div>
    </AbsoluteFill>
  );
};
